import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  KeyRound, Search, Package, ShieldCheck, ShieldOff, Send,
  Copy, Check, Filter, RefreshCw, ExternalLink, History,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/software-keys")({
  head: () => ({
    meta: [{ title: "Software licenses — Admin" }],
  }),
  component: SoftwareKeysAdmin,
});

type KeyRow = {
  id: string;
  key_value: string;
  status: "available" | "issued" | "revoked" | "expired";
  product_id: string;
  variant_id: string | null;
  issued_to_user_id: string | null;
  issued_to_order_item_id: string | null;
  issued_at: string | null;
  expires_at: string | null;
  created_at: string;
  notes: string | null;
  products: { name: string; slug: string } | null;
  profile: { display_name: string | null } | null;
  order_item: {
    id: string;
    order_id: string;
    orders: { guest_email: string | null; user_id: string | null } | null;
  } | null;
};

const statusVariant: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  issued: "default",
  available: "secondary",
  revoked: "destructive",
  expired: "outline",
};

function SoftwareKeysAdmin() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [productFilter, setProductFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [historyKey, setHistoryKey] = useState<KeyRow | null>(null);

  const products = useQuery({
    queryKey: ["admin", "software-products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id, name, slug, active, cover_url")
        .eq("kind", "software")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const keys = useQuery({
    queryKey: ["admin", "license-keys", productFilter, statusFilter],
    queryFn: async () => {
      let q = supabase
        .from("license_keys")
        .select(
          "id, key_value, status, product_id, variant_id, issued_to_user_id, issued_to_order_item_id, issued_at, expires_at, created_at, notes, products:product_id(name, slug), profile:issued_to_user_id(display_name), order_item:issued_to_order_item_id(id, order_id, orders:order_id(guest_email, user_id))"
        )
        .order("created_at", { ascending: false })
        .limit(500);
      if (productFilter !== "all") q = q.eq("product_id", productFilter);
      if (statusFilter !== "all") q = q.eq("status", statusFilter as "available" | "issued" | "revoked");
      const { data, error } = await q;
      if (error) throw error;
      return data as unknown as KeyRow[];
    },
  });

  const stats = useMemo(() => {
    const rows = keys.data ?? [];
    return {
      total: rows.length,
      issued: rows.filter((r) => r.status === "issued").length,
      available: rows.filter((r) => r.status === "available").length,
      revoked: rows.filter((r) => r.status === "revoked").length,
    };
  }, [keys.data]);

  const filtered = useMemo(() => {
    const rows = keys.data ?? [];
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      [
        r.key_value,
        r.products?.name,
        r.profile?.display_name,
        r.order_item?.orders?.guest_email,
        r.notes,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [keys.data, search]);

  const logEvent = async (
    license_key_id: string,
    action: "voided" | "resent" | "returned_to_pool",
    details: Record<string, unknown> = {},
    note?: string,
  ) => {
    const { data: u } = await supabase.auth.getUser();
    await (supabase.from("license_key_events") as any).insert({
      license_key_id,
      action,
      actor_id: u.user?.id ?? null,
      details,
      note: note ?? null,
    });
  };

  const voidMutation = useMutation({
    mutationFn: async (row: KeyRow) => {
      const stamp = new Date().toISOString();
      const { error } = await supabase
        .from("license_keys")
        .update({ status: "revoked" as const, notes: `Revoked ${stamp}` })
        .eq("id", row.id);
      if (error) throw error;
      await logEvent(row.id, "voided", {
        product_id: row.product_id,
        previous_status: row.status,
        issued_to_user_id: row.issued_to_user_id,
      });
    },
    onSuccess: () => {
      toast.success("License key voided");
      qc.invalidateQueries({ queryKey: ["admin", "license-keys"] });
      qc.invalidateQueries({ queryKey: ["license-key-events"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Failed to void key"),
  });

  const reissueMutation = useMutation({
    mutationFn: async (row: KeyRow) => {
      const { error } = await supabase
        .from("license_keys")
        .update({ status: "available" as const, issued_to_user_id: null, issued_to_order_item_id: null, issued_at: null, notes: null })
        .eq("id", row.id);
      if (error) throw error;
      await logEvent(row.id, "returned_to_pool", {
        product_id: row.product_id,
        previous_status: row.status,
        previous_user_id: row.issued_to_user_id,
      });
    },
    onSuccess: () => {
      toast.success("Key returned to pool");
      qc.invalidateQueries({ queryKey: ["admin", "license-keys"] });
      qc.invalidateQueries({ queryKey: ["license-key-events"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  const resendMutation = useMutation({
    mutationFn: async (row: KeyRow) => {
      const email = row.order_item?.orders?.guest_email;
      const stamp = new Date().toISOString();
      const note = `Resent ${stamp}${email ? ` to ${email}` : ""}`;
      const { error } = await supabase
        .from("license_keys")
        .update({ notes: note })
        .eq("id", row.id);
      if (error) throw error;
      await supabase.from("analytics_events").insert({
        event: "license_key.resent",
        properties: { license_id: row.id, product_id: row.product_id, email: email ?? null },
      });
      await logEvent(row.id, "resent", { email: email ?? null, product_id: row.product_id });
      return email;
    },
    onSuccess: (email) => {
      toast.success(email ? `Delivery queued to ${email}` : "Delivery queued");
      qc.invalidateQueries({ queryKey: ["admin", "license-keys"] });
      qc.invalidateQueries({ queryKey: ["license-key-events"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Resend failed"),
  });

  const copy = async (id: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(id);
      toast.success("Copied");
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">Software licenses</h2>
            <p className="text-sm text-muted-foreground">
              Manage software products, browse issued keys, and resend or void licenses.
            </p>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => keys.refetch()}>
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total keys" value={stats.total} icon={KeyRound} />
          <StatCard label="Issued" value={stats.issued} icon={ShieldCheck} accent="text-primary" />
          <StatCard label="In pool" value={stats.available} icon={Package} />
          <StatCard label="Revoked" value={stats.revoked} icon={ShieldOff} accent="text-destructive" />
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Products</CardTitle>
            <CardDescription>Quick links to manage software catalog entries.</CardDescription>
          </CardHeader>
          <CardContent>
            {products.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : !products.data || products.data.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No software products yet.{" "}
                <Link to="/admin/software" className="underline">Create one</Link>.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {products.data.map((p) => (
                  <Link
                    key={p.id}
                    to="/admin/software"
                    className="inline-flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5 text-xs hover:bg-muted"
                  >
                    <Package className="h-3.5 w-3.5" />
                    {p.name}
                    {!p.active && <Badge variant="outline" className="ml-1">inactive</Badge>}
                    <ExternalLink className="h-3 w-3 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Filter className="h-4 w-4" /> Issued keys
            </CardTitle>
            <CardDescription>
              Search by key, product, customer, or email. Resend delivery or void a key.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <div className="relative min-w-[220px] flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search keys, customers, emails…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Select value={productFilter} onValueChange={setProductFilter}>
                <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All products</SelectItem>
                  {products.data?.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="issued">Issued</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="revoked">Revoked</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Key</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Issued</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keys.isLoading ? (
                    <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">No keys found.</TableCell></TableRow>
                  ) : (
                    filtered.map((row) => {
                      const email = row.order_item?.orders?.guest_email;
                      const isIssued = row.status === "issued";
                      return (
                        <TableRow key={row.id}>
                          <TableCell className="font-mono text-xs">
                            <div className="flex items-center gap-1.5">
                              <span className="max-w-[220px] truncate">{row.key_value}</span>
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copy(row.id, row.key_value)}>
                                {copiedId === row.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{row.products?.name ?? "—"}</TableCell>
                          <TableCell className="text-sm">
                            {row.profile?.display_name || email || (row.issued_to_user_id ? row.issued_to_user_id.slice(0, 8) : "—")}
                            {email && <div className="text-xs text-muted-foreground">{email}</div>}
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusVariant[row.status] ?? "secondary"} className="capitalize">{row.status}</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {row.issued_at ? new Date(row.issued_at).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="gap-1.5"
                                onClick={() => setHistoryKey(row)}
                                title="View audit history"
                              >
                                <History className="h-3.5 w-3.5" /> History
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1.5"
                                disabled={!isIssued || resendMutation.isPending}
                                onClick={() => resendMutation.mutate(row)}
                              >
                                <Send className="h-3.5 w-3.5" /> Resend
                              </Button>
                              {isIssued ? (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button size="sm" variant="destructive" className="gap-1.5">
                                      <ShieldOff className="h-3.5 w-3.5" /> Void
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Void this license key?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        The customer will no longer be able to activate <span className="font-mono">{row.key_value}</span>. This cannot be undone from the customer's view.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => voidMutation.mutate(row)}>
                                        Void key
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              ) : row.status === "revoked" ? (
                                <Button size="sm" variant="outline" onClick={() => reissueMutation.mutate(row)}>
                                  Return to pool
                                </Button>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
      <KeyHistoryDialog row={historyKey} onOpenChange={(o: boolean) => !o && setHistoryKey(null)} />
    </>
  );
}

function StatCard({
  label, value, icon: Icon, accent,
}: { label: string; value: number; icon: typeof KeyRound; accent?: string }) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between py-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className={`text-2xl font-semibold ${accent ?? ""}`}>{value}</div>
        </div>
        <Icon className={`h-5 w-5 ${accent ?? "text-muted-foreground"}`} />
      </CardContent>
    </Card>
  );
}

const actionLabel: Record<string, { label: string; tone: string }> = {
  voided: { label: "Voided", tone: "text-destructive" },
  resent: { label: "Resent delivery", tone: "text-primary" },
  returned_to_pool: { label: "Returned to pool", tone: "text-amber-500" },
  reissued: { label: "Reissued", tone: "text-primary" },
  note: { label: "Note", tone: "text-muted-foreground" },
};

function KeyHistoryDialog({
  row, onOpenChange,
}: { row: KeyRow | null; onOpenChange: (o: boolean) => void }) {
  const { data, isLoading } = useQuery({
    enabled: !!row,
    queryKey: ["license-key-events", row?.id],
    queryFn: async () => {
      const { data: events, error } = await (supabase.from("license_key_events") as any)
        .select("id, action, actor_id, details, note, created_at")
        .eq("license_key_id", row!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const actorIds = Array.from(new Set((events ?? []).map((e: any) => e.actor_id).filter(Boolean))) as string[];
      let profiles: Record<string, string> = {};
      if (actorIds.length) {
        const { data: pdata } = await supabase.from("profiles").select("id, display_name").in("id", actorIds);
        profiles = Object.fromEntries((pdata ?? []).map((p) => [p.id, p.display_name ?? "Admin"]));
      }
      return (events ?? []).map((e: any) => ({ ...e, actor_name: e.actor_id ? profiles[e.actor_id] ?? "Admin" : "System" }));
    },
  });

  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4" /> License audit history
          </DialogTitle>
          {row && (
            <DialogDescription className="font-mono text-xs break-all">{row.key_value}</DialogDescription>
          )}
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading history…</p>
          ) : !data || data.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No actions recorded yet. Future void / resend / return actions will appear here.
            </p>
          ) : (
            <ol className="relative space-y-4 border-l border-border pl-5">
              {data.map((e: any) => {
                const meta = actionLabel[e.action] ?? { label: e.action, tone: "text-foreground" };
                const email = e.details?.email as string | undefined;
                return (
                  <li key={e.id} className="relative">
                    <span className="absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary ring-4 ring-background" />
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className={`text-sm font-medium ${meta.tone}`}>{meta.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(e.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      by <span className="font-medium text-foreground">{e.actor_name}</span>
                      {email && <> · sent to {email}</>}
                    </div>
                    {e.note && <p className="mt-1 text-xs">{e.note}</p>}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
